import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agentsService from '@/services/agents.service';
import { PinnedAgent } from '@/types/agents';
import AgentAvatar from '@/components/AgentAvatar';
import { routeForAgent } from '@/utils/agentRoute';

/**
 * Seção "Meus agentes" do dashboard (J05): cards de atalho pros agentes que o
 * usuário fixou. Busca os próprios dados (independente do dashboard) e some quando
 * não há nada fixado — pra não poluir o dashboard de quem não usa agentes (Decisão J05).
 * A leitura já vem filtrada pela ACL vigente no backend (perder acesso → o card some).
 */

const PinnedAgentsSection: FC = () => {
  const navigate = useNavigate();
  const [pins, setPins] = useState<PinnedAgent[]>([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      setPins(await agentsService.listPinned());
    } catch {
      setPins([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUnpin = useCallback(
    async (agentId: string) => {
      setPins((prev) => prev.filter((p) => p.agent_id !== agentId)); // otimista
      try {
        await agentsService.unpin(agentId);
      } catch {
        load(); // reverte buscando o estado real
      }
    },
    [load],
  );

  // Enquanto carrega ou quando não há nada fixado, a seção não aparece.
  if (!loaded || pins.length === 0) return null;

  return (
    <section data-testid="pinned-agents-section">
      <h2 className="text-content-primary text-lg font-bold mb-3 flex items-center gap-2">
        <span className="material-symbols-outlined text-accent">push_pin</span>
        Meus agentes
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {pins.map((pin) => (
          <div
            key={pin.id}
            data-testid="pinned-agent-card"
            className="group relative flex items-center gap-3 rounded-2xl bg-surface-secondary border border-surface-border hover:border-accent/60 hover:shadow-lg transition p-4"
          >
            <button
              type="button"
              onClick={() => navigate(routeForAgent(pin.type, pin.agent_id))}
              className="flex items-center gap-3 flex-1 min-w-0 text-left"
            >
              <AgentAvatar avatar={pin.avatar} sizeClass="w-11 h-11" textClass="text-2xl" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-content-primary truncate">{pin.name}</h3>
                <span className="text-xs text-accent opacity-0 group-hover:opacity-100 transition">Abrir →</span>
              </div>
            </button>
            <button
              type="button"
              data-testid="unpin-button"
              aria-label={`Desafixar ${pin.name}`}
              title="Desafixar"
              onClick={() => handleUnpin(pin.agent_id)}
              className="shrink-0 grid place-items-center w-8 h-8 rounded-lg text-content-tertiary hover:text-red-400 hover:bg-surface-tertiary transition"
            >
              <span className="material-symbols-outlined text-lg">close</span>
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

export default PinnedAgentsSection;
